import { AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box, Center, Flex, IconButton, Input, Menu, MenuButton, MenuItem, MenuList, MenuDivider, Text, useColorModeValue, Portal, Grid, GridItem } from "@chakra-ui/react"
import { faCancel, faCheck, faCrown, faEllipsis, faFolderClosed, faListCheck, faPen, faFolderPlus, faSmile, faVideo, faImage, faFilePdf, faMusic, faBolt, faTrash, faTimes } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { AppMeta, NewContentItem, NewMediaReference, NewSection } from "@xnav/types/src/common"
import React, { memo, MouseEventHandler, useEffect, useMemo, useState } from "react"
import ContentCard from "./contentCard"
// import Draggable from "./draggable"
import Draggable from 'react-draggable';
import { useApp, useDragging, useSelectionReference, useDeletedContent, useSetContentStore } from "@/hooks/contextHooks"
import { ReactStateType } from "@xnav/types/src/frontend"
import { validateNameisUnique } from "@/functions/editorFunctions"
import { v4 } from "uuid"

function applyPremiumRecurse(contentItem: NewContentItem, tier: number): NewContentItem {
	if (contentItem.type !== 'section') return {
		...contentItem,
		premium: contentItem.premium ?? 0 > tier ? contentItem.premium : tier
	}
	return {
		...contentItem,
		premium: tier,
		contents: contentItem.contents.map(s => applyPremiumRecurse(s, tier))
	} as NewSection
}

function setPremiumRecurse(contentItem: NewContentItem, tier: number | undefined): NewContentItem {
	if (contentItem.type !== 'section') return {
		...contentItem,
		premium: tier
	}
	return {
		...contentItem,
		premium: tier,
		contents: contentItem.contents.map(s => setPremiumRecurse(s, tier))
	} as NewSection
}

function isDescendant(parent: NewSection, childId: string): boolean {
	if (!parent.contents) return false;
	for (const item of parent.contents) {
		if (item.id === childId) return true;
		if (item.type === 'section' && isDescendant(item as NewSection, childId)) {
			return true;
		}
	}
	return false;
}

const SectionCardComponent: React.FC<{
	index: number,
	sectionMeta: NewSection,
	deletedContent: string[],
	validateName: (name: string) => boolean,
	onChange: (newSection: NewSection) => void,
	onDelete: (index: number) => void,
	onMoveItem: (draggedId: string, targetId: string, position: 'inside' | 'before' | 'after') => void,
	rootSection?: boolean,
	searchQuery?: string
}> = ({ index, sectionMeta, validateName, onChange, onDelete, onMoveItem, rootSection, searchQuery }) => {
	const [dragging, setDragging] = useDragging()
	const app = useApp() as AppMeta

	const [newSubSection, setNewSubSection] = useState<NewSection>()
	const [newContent, setNewContent] = useState<NewMediaReference>()
	const [confirmDeletion, setConfirmDeletion] = useState<boolean>(false)
	const [newName, setNewName] = useState<string>()
	const [isHovering, setIsHovering] = useState<boolean>(false)
	const [isOpen, setIsOpen] = useState<boolean>(false)
	const [isValid, setIsValid] = useState<boolean>(false)
	const [isDraggingSelf, setIsDraggingSelf] = useState<boolean>(false)
	const [dropPosition, setDropPosition] = useState<'before' | 'after' | 'inside' | null>(null)

	useEffect(() => {
		if (searchQuery) {
			setIsOpen(true)
		}
	}, [searchQuery])

	const [selectionReference, setSelectionReference] = useSelectionReference() as ReactStateType<NewContentItem>
	const setContentStore = useSetContentStore()

	useEffect(() => {
		if (selectionReference && sectionMeta) {
			const hasItem = (s: NewSection): boolean => {
				if (s.id === selectionReference.id) return true;
				if (s.contents) {
					for (const c of s.contents) {
						if (c.id === selectionReference.id) return true;
						if (c.type === 'section' && hasItem(c as NewSection)) return true;
					}
				}
				return false;
			};
			if (hasItem(sectionMeta)) {
				setIsOpen(true);
			}
		}
	}, [selectionReference, sectionMeta]);
	const [deletedContent, setDeletedContent] = useDeletedContent()

	const premiumColor = useMemo(() => {
		switch (sectionMeta.premium) {
			case 1: return "#48BB78" // green.400
			case 2: return "#4299E1" // blue.400
			case 3: return "#ECC94B" // yellow.400
			default: return "gray"
		}
	}, [sectionMeta.premium])

	function handleSectionSelect() {
		setSelectionReference(sectionMeta)
	}

	const handleDragStart = (e: React.DragEvent) => {
		e.stopPropagation()
		e.dataTransfer.setData("text/plain", sectionMeta.id)
		e.dataTransfer.effectAllowed = "move"
		setDragging({
			id: sectionMeta.id,
			data: sectionMeta,
			onDragEnd: () => {}
		})
		setTimeout(() => {
			setIsDraggingSelf(true)
		}, 0)
	}

	const handleDragEnd = (e: React.DragEvent) => {
		setDragging(undefined)
		setIsDraggingSelf(false)
	}

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()

		if (dragging) {
			if (dragging.id === sectionMeta.id) return
			if (dragging.data.type === 'section' && isDescendant(dragging.data as NewSection, sectionMeta.id)) {
				return
			}
		}

		const rect = e.currentTarget.getBoundingClientRect()
		const relativeY = e.clientY - rect.top
		const height = rect.height

		let position: 'before' | 'after' | 'inside' = 'inside'
		if (relativeY < height * 0.25) {
			position = 'before'
		} else if (relativeY > height * 0.75) {
			position = 'after'
		} else {
			position = 'inside'
		}

		setDropPosition(position)
	}

	const handleDragLeave = (e: React.DragEvent) => {
		setDropPosition(null)
	}

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setDropPosition(null)

		const draggedId = e.dataTransfer.getData("text/plain") || (dragging as any)?.id
		if (draggedId && draggedId !== sectionMeta.id) {
			if (dragging && dragging.data.type === 'section' && isDescendant(dragging.data as NewSection, sectionMeta.id)) {
				return
			}

			const rect = e.currentTarget.getBoundingClientRect()
			const relativeY = e.clientY - rect.top
			const height = rect.height

			let position: 'before' | 'after' | 'inside' = 'inside'
			if (relativeY < height * 0.25) {
				position = 'before'
			} else if (relativeY > height * 0.75) {
				position = 'after'
			} else {
				position = 'inside'
			}

			onMoveItem(draggedId, sectionMeta.id, position)
		}
	}

	function validateNameInSection(name: string) {
		for (const contentItem of sectionMeta.contents) {
			if (contentItem.title === name) return false
		}
		return true
	}

	useEffect(() => {
		if (newSubSection) setIsValid(validateName(newSubSection.title))
	}, [newSubSection])

	useEffect(() => {
		if (newContent) setIsValid(validateNameInSection(newContent.title))
	}, [newContent])

	useEffect(() => {
		if (newName) setIsValid(validateNameInSection(newName))
	}, [newName])



	const bg = useColorModeValue("white", "gray.800")
	const draggingBg = useColorModeValue("blue.50", "blue.900")
	const borderColor = useColorModeValue("gray.200", "gray.600")

	const outlineStyle = dropPosition === 'inside' ? "2px dashed #00e5ff" : undefined
	const isActive = selectionReference && selectionReference.id === sectionMeta.id && selectionReference.type === 'section'
	const bgHighlight = isActive 
		? useColorModeValue("teal.50", "rgba(16, 185, 129, 0.15)") 
		: (dropPosition === 'inside' ? useColorModeValue("cyan.50", "rgba(6, 182, 212, 0.15)") : undefined)

	return (

		<AccordionItem
			sx={{
				border: "none",
				borderRadius: "lg",
				mb: 2,
				transition: "all 0.2s"
			}}
			border="none"
		>
			<AccordionButton
				as="div"
				onClick={() => {
					setIsOpen(!isOpen)
					if (selectionReference?.type === 'section') {
						setSelectionReference(undefined)
					}
				}}
				onMouseEnter={() => {
					setIsHovering(dragging === undefined || dragging.id !== sectionMeta.id)
				}}
				onMouseLeave={() => setIsHovering(false)}
				draggable={true}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				borderRadius="lg"
				_hover={{ bg: isActive ? useColorModeValue("teal.50", "rgba(16, 185, 129, 0.15)") : useColorModeValue("gray.100", "gray.700") }}
				p={2}
				cursor="pointer"
				position="relative"
				style={{
					opacity: isDraggingSelf ? 0.4 : 1,
					outline: outlineStyle,
					outlineOffset: dropPosition === 'inside' ? "-2px" : undefined,
					background: bgHighlight
				}}
			>
				{isActive && (
					<Box position="absolute" left={0} top={0} bottom={0} w="4px" bg="teal.500" borderTopLeftRadius="md" borderBottomLeftRadius="md" />
				)}
				{dropPosition === 'before' && (
					<Box position="absolute" top="-2px" left="0" right="0" height="4px" bgGradient="linear(to-r, #06b6d4, #10b981)" zIndex="10" borderRadius="full" pointerEvents="none" />
				)}
				{dropPosition === 'after' && (
					<Box position="absolute" bottom="-2px" left="0" right="0" height="4px" bgGradient="linear(to-r, #06b6d4, #10b981)" zIndex="10" borderRadius="full" pointerEvents="none" />
				)}
				<AccordionIcon opacity={isHovering || isOpen ? 1 : 0} color="gray.500" />
				<FontAwesomeIcon
					size='sm'
					icon={faFolderClosed}
					color={useColorModeValue("#ECC94B", "#D69E2E")} // Yellow folder color
					style={{ marginRight: '8px' }}
				/>
				{sectionMeta.premium ? (
					<FontAwesomeIcon
						size='sm'
						icon={faCrown}
						color={premiumColor}
					/>
				) : (<></>)}
				{!newSubSection && !newContent && !confirmDeletion && newName === undefined && (
					<>
						<Text overflow='hidden' textAlign='left' flex={1} title={sectionMeta.title}>{sectionMeta.title}</Text>
						<Box onClick={(e) => e.stopPropagation()}>
							<Menu>
								<MenuButton opacity={isHovering ? 1 : 0} as={IconButton} size='xs' aria-label="Section Options" icon={<FontAwesomeIcon icon={faEllipsis} />} />
								<Portal>
									<MenuList
										bg={useColorModeValue("rgba(255, 255, 255, 0.95)", "rgba(26, 32, 44, 0.95)")}
										backdropFilter="blur(15px)"
										border="1px solid"
										borderColor={useColorModeValue("rgba(226, 232, 240, 0.8)", "rgba(74, 85, 104, 0.8)")}
										borderRadius="xl"
										boxShadow="xl"
										p={3}
										minW="460px"
									>
										<Grid templateColumns="1fr 1fr" gap={4}>
											{/* Left Column: Structure, Access, Danger */}
											<GridItem display="flex" flexDir="column" gap={2}>
												<Box>
													<Text px={3} py={1} fontSize="10px" fontWeight="black" color="teal.500" _dark={{ color: "teal.300" }} textTransform="uppercase" letterSpacing="wider">
														Structure
													</Text>
													<MenuItem
														icon={<FontAwesomeIcon icon={faPen} />}
														onClick={() => setNewName(sectionMeta.title)}
														borderRadius="lg"
														_hover={{ bg: useColorModeValue("teal.50", "rgba(20, 184, 166, 0.15)"), color: useColorModeValue("teal.700", "teal.200") }}
													>
														Rename
													</MenuItem>
													<MenuItem
														icon={<FontAwesomeIcon icon={faFolderPlus} />}
														onClick={() =>
															setNewSubSection({ title: '', id: v4(), contents: [], type: 'section' })
														}
														borderRadius="lg"
														_hover={{ bg: useColorModeValue("teal.50", "rgba(20, 184, 166, 0.15)"), color: useColorModeValue("teal.700", "teal.200") }}
													>
														Add Sub-Section
													</MenuItem>
													<MenuItem
														icon={<FontAwesomeIcon icon={faImage} />}
														onClick={handleSectionSelect}
														borderRadius="lg"
														_hover={{ bg: useColorModeValue("teal.50", "rgba(20, 184, 166, 0.15)"), color: useColorModeValue("teal.700", "teal.200") }}
													>
														Edit Section Icon
													</MenuItem>
												</Box>

												<MenuDivider borderColor={useColorModeValue("gray.100", "gray.700")} />

												<Box>
													<Text px={3} py={1} fontSize="10px" fontWeight="black" color="yellow.600" _dark={{ color: "yellow.300" }} textTransform="uppercase" letterSpacing="wider">
														Access Control
													</Text>
													<MenuItem
														icon={<FontAwesomeIcon icon={faCrown} />}
														onClick={() => onChange(setPremiumRecurse(sectionMeta, 1) as NewSection)}
														borderRadius="lg"
														_hover={{ bg: useColorModeValue("yellow.50", "rgba(236, 201, 75, 0.15)"), color: useColorModeValue("yellow.700", "yellow.200") }}
													>
														Set Level 1
													</MenuItem>
													<MenuItem
														icon={<FontAwesomeIcon icon={faCrown} />}
														onClick={() => onChange(setPremiumRecurse(sectionMeta, 2) as NewSection)}
														borderRadius="lg"
														_hover={{ bg: useColorModeValue("yellow.50", "rgba(236, 201, 75, 0.15)"), color: useColorModeValue("yellow.700", "yellow.200") }}
													>
														Set Level 2
													</MenuItem>
													<MenuItem
														icon={<FontAwesomeIcon icon={faCrown} />}
														onClick={() => onChange(setPremiumRecurse(sectionMeta, 3) as NewSection)}
														borderRadius="lg"
														_hover={{ bg: useColorModeValue("yellow.50", "rgba(236, 201, 75, 0.15)"), color: useColorModeValue("yellow.700", "yellow.200") }}
													>
														Set Level 3
													</MenuItem>
													{sectionMeta.premium && (
														<MenuItem
															icon={<FontAwesomeIcon icon={faTimes} />}
															onClick={() => onChange(setPremiumRecurse(sectionMeta, undefined) as NewSection)}
															borderRadius="lg"
															_hover={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.1)"), color: useColorModeValue("gray.700", "gray.300") }}
														>
															Clear Level
														</MenuItem>
													)}
												</Box>

												<MenuDivider borderColor={useColorModeValue("gray.100", "gray.700")} />

												<MenuItem
													icon={<FontAwesomeIcon icon={faTrash} />}
													onClick={() => setConfirmDeletion(true)}
													borderRadius="lg"
													color="red.500"
													_hover={{ bg: useColorModeValue("red.50", "rgba(229, 62, 62, 0.15)"), color: "red.600" }}
												>
													Delete Section
												</MenuItem>
											</GridItem>

											{/* Right Column: Add Content */}
											<GridItem display="flex" flexDir="column" gap={2}>
												<Box>
													<Text px={3} py={1} fontSize="10px" fontWeight="black" color="blue.500" _dark={{ color: "blue.300" }} textTransform="uppercase" letterSpacing="wider">
														Add Content
													</Text>
													<MenuItem
														icon={<FontAwesomeIcon icon={faVideo} />}
														onClick={() => setNewContent({ type: 'video', id: v4(), title: '' })}
														borderRadius="lg"
														_hover={{ bg: useColorModeValue("blue.50", "rgba(66, 153, 225, 0.15)"), color: useColorModeValue("blue.700", "blue.200") }}
													>
														Add New Video
													</MenuItem>
													<MenuItem
														icon={<FontAwesomeIcon icon={faImage} />}
														onClick={() => setNewContent({ type: 'image', id: v4(), title: '' })}
														borderRadius="lg"
														_hover={{ bg: useColorModeValue("blue.50", "rgba(66, 153, 225, 0.15)"), color: useColorModeValue("blue.700", "blue.200") }}
													>
														Add New Image
													</MenuItem>
													<MenuItem
														icon={<FontAwesomeIcon icon={faFilePdf} />}
														onClick={() => setNewContent({ type: 'pdf', id: v4(), title: '' })}
														borderRadius="lg"
														_hover={{ bg: useColorModeValue("blue.50", "rgba(66, 153, 225, 0.15)"), color: useColorModeValue("blue.700", "blue.200") }}
													>
														Add New PDF
													</MenuItem>
													<MenuItem
														icon={<FontAwesomeIcon icon={faMusic} />}
														onClick={() => setNewContent({ type: 'audio', id: v4(), title: '' })}
														borderRadius="lg"
														_hover={{ bg: useColorModeValue("blue.50", "rgba(66, 153, 225, 0.15)"), color: useColorModeValue("blue.700", "blue.200") }}
													>
														Add New Audio
													</MenuItem>
													<MenuItem
														icon={<FontAwesomeIcon icon={faBolt} />}
														onClick={() => setNewContent({ type: 'flashcard', id: v4(), title: '' })}
														borderRadius="lg"
														_hover={{ bg: useColorModeValue("blue.50", "rgba(66, 153, 225, 0.15)"), color: useColorModeValue("blue.700", "blue.200") }}
													>
														Add New Flashcards
													</MenuItem>
													<MenuItem
														icon={<FontAwesomeIcon icon={faListCheck} />}
														onClick={() => setNewContent({ type: 'quiz', id: v4(), title: '' })}
														borderRadius="lg"
														_hover={{ bg: useColorModeValue("blue.50", "rgba(66, 153, 225, 0.15)"), color: useColorModeValue("blue.700", "blue.200") }}
													>
														Add New Quiz
													</MenuItem>
												</Box>
											</GridItem>
										</Grid>
									</MenuList>
								</Portal>
							</Menu>
						</Box>
					</>
				)}
				{newName !== undefined && (
					<Flex w='full' gap={1} align="center" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
						<Input size='xs' variant="outline" w="full" placeholder='Rename section...' autoFocus={true} value={newName} onChange={(e) => setNewName(e.target.value)} bg="white" _dark={{ bg: "gray.800" }} />
						<IconButton size='xs' isDisabled={!isValid} variant='solid' colorScheme="blue" aria-label="Confirm Rename" icon={<FontAwesomeIcon icon={faCheck} />} onClick={(e) => {
							e.stopPropagation()
							onChange({ ...sectionMeta, title: newName })
							setNewName(undefined)
						}} />
						<IconButton size='xs' variant='ghost' colorScheme="red" aria-label="Cancel Rename" icon={<FontAwesomeIcon icon={faCancel} />} onClick={(e) => {
							e.stopPropagation()
							setNewName(undefined)
						}} />
					</Flex>
				)}
				{newSubSection && (
					<Flex w='full' gap={1} align="center" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
						<Input size='xs' variant="outline" w="full" placeholder='Sub-Section Title...' autoFocus={true} value={newSubSection.title} onChange={(e) => setNewSubSection({ ...newSubSection, title: e.target.value })} bg="white" _dark={{ bg: "gray.800" }} />
						<IconButton size='xs' isDisabled={!isValid} variant='solid' colorScheme="blue" aria-label="Confirm" icon={<FontAwesomeIcon icon={faCheck} />} onClick={(e) => {
							e.stopPropagation()
							onChange({ ...sectionMeta, contents: [...sectionMeta.contents, newSubSection] })
							setNewSubSection(undefined)
						}} />
						<IconButton size='xs' variant='ghost' colorScheme="red" aria-label="Cancel" icon={<FontAwesomeIcon icon={faCancel} />} onClick={(e) => {
							e.stopPropagation()
							setNewSubSection(undefined)
						}} />
					</Flex>
				)}
				{newContent && (
					<Flex w='full' gap={1} align="center" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
						<Input size='xs' variant="outline" w="full" placeholder='Content Title...' autoFocus={true} value={newContent.title} onChange={(e) => setNewContent({ ...newContent, title: e.target.value })} bg="white" _dark={{ bg: "gray.800" }} />
						<IconButton size='xs' isDisabled={!isValid} variant='solid' colorScheme="blue" aria-label="Confirm" icon={<FontAwesomeIcon icon={faCheck} />} onClick={(e) => {
							e.stopPropagation()
							onChange({ ...sectionMeta, contents: [...sectionMeta.contents, newContent] })
							if (newContent.type === 'video') {
								setContentStore(prev => ({
									...prev,
									[newContent.id]: {
										id: newContent.id,
										title: newContent.title,
										polygons: [],
										hotSpots: [],
										wisdomDocTitles: [],
										clientScale: 1
									}
								}));
							}
							setSelectionReference(newContent)
							setNewContent(undefined)
						}} />
						<IconButton size='xs' variant='ghost' colorScheme="red" aria-label="Cancel" icon={<FontAwesomeIcon icon={faCancel} />} onClick={(e) => {
							e.stopPropagation()
							setNewContent(undefined)
						}} />
					</Flex>
				)}
				{confirmDeletion && (
					<Flex w='full' gap={1} align="center" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
						<Text fontSize="xs" color="red.600" _dark={{ color: "red.200" }} flex={1} textAlign="left" fontWeight="bold">Delete?</Text>
						<IconButton size='xs' variant='solid' colorScheme="red" aria-label="Confirm Delete" icon={<FontAwesomeIcon icon={faCheck} />} onClick={(e) => {
							e.stopPropagation()
							onDelete(index)
							setConfirmDeletion(false)
						}} />
						<IconButton size='xs' variant='ghost' colorScheme="gray" aria-label="Cancel Delete" icon={<FontAwesomeIcon icon={faCancel} />} onClick={(e) => {
							e.stopPropagation()
							setConfirmDeletion(false)
						}} />
					</Flex>
				)}
			</AccordionButton>
			<AccordionPanel>
				{sectionMeta.contents.map((content, index) => content.type === 'section' ? (
					<SectionCard
						key={content.id}
						sectionMeta={content}
						deletedContent={deletedContent}
						index={index}
						validateName={(name: string) => validateNameisUnique(name, sectionMeta.contents.map(c => c.title))}
						onMoveItem={onMoveItem}
						searchQuery={searchQuery}
						onChange={(sm) => {
							onChange({ ...sectionMeta, contents: sectionMeta.contents.map(s => s.id === sm.id ? sm : s) })
						}}
						onDelete={(index: number) => {
							setDeletedContent(prev => [...prev, content.id]);
							console.log("Deleting item at index:", index, content, selectionReference);
							const updatedContents = sectionMeta.contents.filter((_, i) => i !== index);
							console.log("Updated contents:", updatedContents);
							onChange({ ...sectionMeta, contents: updatedContents });
						}}
					/>
				) : (
					<ContentCard
						key={content.id}
						contentMeta={content}
						index={index}
						validateName={(name) => validateNameisUnique(name, sectionMeta.contents.map(c => c.title))}
						onDelete={(index: number) => {
							setDeletedContent(prev => [...prev, content.id]);
							console.log("Deleting item at index:", index, content, selectionReference);
							const updatedContents = sectionMeta.contents.filter((_, i) => i !== index);
							console.log("Updated contents:", updatedContents);
							onChange({ ...sectionMeta, contents: updatedContents });
						}}
						onChange={(newContent) => onChange({
							...sectionMeta,
							contents: sectionMeta.contents.map(s => s.id === newContent.id ? newContent : s)
						})}
						onMoveItem={onMoveItem}
					/>
				))}
			</AccordionPanel>
		</AccordionItem>
	)
}

const SectionCard = memo(SectionCardComponent)

export default SectionCard
